import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Bot, Send, ArrowRight, Sparkles, CheckCircle2, Loader2 } from 'lucide-react';
import { createLead } from '../lib/marketingApi';
import { isValidPhone } from '../lib/phone';
import { cn } from '@/lib/utils';

// Curated illustrative responses. Keys are the exact example questions.
const CURATED = {
  'Which vendors need my attention this week?': {
    tools: ['Checking overdue bills', 'Checking missing expected bills', 'Comparing payment exceptions'],
    answer:
      'Six vendors need attention in this illustrative workspace. Two have bills more than 30 days overdue, three have expected bills missing, and one has a payment mismatch. TECO is the highest priority because it has both overdue and missing bills.',
    evidence: '18 overdue bills • 6 vendors • 3 BillPortal sources',
    chips: ['Break it down by property', 'Create an overdue-bills CSV', 'Draft vendor follow-ups'],
  },
  'Which expected bills are missing?': {
    tools: ['Loading the missing-bills forecast', 'Matching against received bills'],
    answer:
      'Eleven expected bills have not arrived in this illustrative workspace. Electric for 4 properties and water for 3 are the largest gaps, and two accounts have been missing for more than two cycles.',
    evidence: '11 missing • 7 properties • 2 aged accounts',
    chips: ['Show the aged accounts', 'Generate a missing-bills report'],
  },
  'Create a CSV of overdue electric bills by property.': {
    tools: ['Filtering overdue electric bills', 'Grouping by property', 'Building CSV'],
    answer:
      'Your CSV is ready — 42 overdue electric bills across 19 properties, grouped by property with balance, due date and days overdue.',
    evidence: '42 rows • 19 properties • CSV',
    chips: ['Email this to my team', 'Draft follow-ups for these vendors'],
  },
  "Why doesn't this payment file match?": {
    tools: ['Loading the payment file', 'Reconciling against bills', 'Explaining the variance'],
    answer:
      'The payment file is $1,284.50 higher than the matched bills in this illustrative example. Three line items include a late fee not present on the original bill, and one account was paid twice.',
    evidence: '$1,284.50 variance • 4 line items',
    chips: ['Show the duplicate payment', 'Open the reconciliation view'],
  },
  'Compare our payment files and fund pulls year to date.': {
    tools: ['Loading payment files', 'Loading credit/debit report', 'Comparing YTD totals'],
    answer:
      'Year to date, payment files total $2.41M and fund pulls total $2.39M in this illustrative workspace — a $19,300 timing difference concentrated in the last two runs.',
    evidence: '$2.41M vs $2.39M • $19.3K timing gap',
    chips: ['Break down by month', 'Which runs are unmatched?'],
  },
  'Help me migrate from our current utility platform.': {
    tools: ['Starting a migration journey', 'Reviewing your export', 'Staging records for review'],
    answer:
      'I can guide the migration step by step. Import your export of properties, meters and accounts and I will map columns, link records, deduplicate and stage everything for your review before anything is onboarded.',
    evidence: 'Preview-first • You approve once',
    chips: ['Sample our current portal first', 'What files do you need?'],
  },
  'Which collector credentials still need information?': {
    tools: ['Reading your onboarding file', 'Validating rows', 'Matching suppliers'],
    answer:
      'Of 60 rows in this illustrative file, 48 are ready to onboard, 9 need a supplier match, and 3 are missing a username or portal URL. Passwords are never shown in chat.',
    evidence: '48 ready • 9 to match • 3 incomplete',
    chips: ['Show the 3 incomplete rows', 'Onboard the ready ones'],
  },
  'Which properties have the greatest emissions impact?': {
    tools: ['Loading emissions & grid factors', 'Ranking properties by impact'],
    answer:
      'The top five properties account for 38% of portfolio emissions in this illustrative workspace. Two are older assets on high-carbon grids and would benefit most from an efficiency or procurement change.',
    evidence: 'Top 5 = 38% of emissions',
    chips: ['Recommend a 2030 target', 'Create a sustainability PDF'],
  },
  'Create a portfolio sustainability report.': {
    tools: ['Gathering emissions data', 'Ranking high-impact properties', 'Building branded PDF'],
    answer:
      'Your portfolio sustainability report is ready in this illustrative example — 128 properties, 12,450 tCO₂e for the trailing 12 months, with the top 10 properties highlighted for decarbonization.',
    evidence: '128 properties • 12,450 tCO₂e • PDF',
    chips: ['Recommend an emissions target', 'Which properties are ENERGY STAR-ready?'],
  },
};

const GROUPS = [
  { title: 'Operations', questions: ['Which vendors need my attention this week?', 'Which expected bills are missing?', 'Create a CSV of overdue electric bills by property.'] },
  { title: 'Payments', questions: ["Why doesn't this payment file match?", 'Compare our payment files and fund pulls year to date.'] },
  { title: 'Onboarding', questions: ['Help me migrate from our current utility platform.', 'Which collector credentials still need information?'] },
  { title: 'Sustainability', questions: ['Which properties have the greatest emissions impact?', 'Create a portfolio sustainability report.'] },
];

const FALLBACK =
  'This preview uses example questions to show how Astrid works. Sign in to ask Astrid about your own BillPortal workspace.';

export default function AstridDrawer({ open, onClose, initialQuestion }) {
  const [messages, setMessages] = useState([]); // {from:'astrid'|'user', ...}
  const [thinking, setThinking] = useState(null); // {tools:[], done:number}
  const [input, setInput] = useState('');
  const [demoView, setDemoView] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoDone, setDemoDone] = useState(false);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const triggerRef = useRef(null);
  const bottomRef = useRef(null);
  const thinkTimer = useRef(null);

  const reset = useCallback(() => {
    setMessages([{ from: 'astrid', text: "Hi, I'm Astrid. Choose an example question below to see how I can help with bills, payments, onboarding and sustainability." }]);
    setThinking(null);
    setInput('');
    setDemoView(false);
    setDemoForm({ name: '', email: '', company: '', phone: '' });
    setDemoLoading(false);
    setDemoDone(false);
    clearTimeout(thinkTimer.current);
  }, []);

  const submitDemo = async (e) => {
    e.preventDefault();
    const { name, email, company, phone } = demoForm;
    if (![name, email, company, phone].every((v) => String(v || '').trim())) {
      toast.error('Please complete all fields.');
      return;
    }
    if (!isValidPhone(phone)) {
      toast.error('Please enter a valid US, UK or Australian phone number.');
      return;
    }
    setDemoLoading(true);
    try {
      await createLead({
        name, email, company, phone,
        segment: 'other',
        interest: 'astrid_ai_agent',
        portfolio_size: 'not_specified',
        message: 'Requested an Astrid demo ("See Astrid on your data") from the Astrid page.',
        source: 'astrid_demo',
        page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      setDemoDone(true);
      toast.success('Thanks — our team will reach out to set up your Astrid demo.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setDemoLoading(false);
    }
  };

  const ask = useCallback((q) => {
    const query = (q || '').trim();
    if (!query) return;
    clearTimeout(thinkTimer.current);
    setMessages((m) => [...m, { from: 'user', text: query }]);

    const curated = CURATED[query];
    if (!curated) {
      setTimeout(() => setMessages((m) => [...m, { from: 'astrid', text: FALLBACK, fallback: true }]), 350);
      return;
    }

    // Step through tool states, then reveal the answer.
    const tools = curated.tools;
    setThinking({ tools, done: 0 });
    let i = 0;
    const step = () => {
      i += 1;
      if (i < tools.length) {
        setThinking({ tools, done: i });
        thinkTimer.current = setTimeout(step, 700);
      } else {
        setThinking({ tools, done: tools.length });
        thinkTimer.current = setTimeout(() => {
          setThinking(null);
          setMessages((m) => [...m, { from: 'astrid', ...curated, isAnswer: true }]);
        }, 650);
      }
    };
    thinkTimer.current = setTimeout(step, 700);
  }, []);

  // Open / close lifecycle: lock scroll, focus, seed conversation.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      reset();
      document.body.style.overflow = 'hidden';
      setTimeout(() => closeRef.current?.focus(), 30);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open, reset]);

  // Seed the initial question when provided.
  useEffect(() => {
    if (open && initialQuestion) {
      const t = setTimeout(() => ask(initialQuestion), 450);
      return () => clearTimeout(t);
    }
  }, [open, initialQuestion, ask]);

  // Autoscroll.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking]);

  // Keyboard: Escape + focus trap.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); handleClose(); return; }
    if (e.key === 'Tab') {
      const focusables = panelRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  const handleClose = () => {
    onClose?.();
    setTimeout(() => triggerRef.current?.focus?.(), 0);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Ask Astrid an example" onKeyDown={onKeyDown}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm bp-fade"
        onClick={handleClose}
        data-testid="astrid-drawer-backdrop"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        className="absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-2xl bp-slide-in dark:bg-[#0d0d12] sm:max-w-[520px]"
        data-testid="astrid-drawer"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white">
            <Bot className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900 dark:text-white">Astrid</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                <Sparkles className="h-3 w-3" /> Example mode
              </span>
            </div>
            <div className="text-[11px] text-slate-400">Illustrative answers — no customer data used.</div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={handleClose}
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
            aria-label="Close"
            data-testid="astrid-drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {demoView ? (
          <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="astrid-demo-request">
            {demoDone ? (
              <div className="flex flex-col items-center gap-4 pt-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Request received</h3>
                <p className="max-w-xs text-sm text-slate-600 dark:text-slate-300">Thanks! Our team will reach out to set up a live Astrid demo on your BillPortal workspace.</p>
                <button type="button" onClick={handleClose} className="mt-2 rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-600" data-testid="astrid-demo-done-close">Done</button>
              </div>
            ) : (
              <>
                <button type="button" onClick={() => setDemoView(false)} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-brand" data-testid="astrid-demo-back">
                  <ArrowRight className="h-3.5 w-3.5 rotate-180" /> Back to examples
                </button>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">See Astrid on your data</h3>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">Tell us where to reach you and we'll set up a live demo of Astrid working inside your own BillPortal workspace.</p>
                <form onSubmit={submitDemo} noValidate className="mt-5 space-y-3.5">
                  {[
                    { k: 'name', label: 'Full name', type: 'text', ph: 'Jane Doe' },
                    { k: 'email', label: 'Work email', type: 'email', ph: 'jane@company.com' },
                    { k: 'company', label: 'Company', type: 'text', ph: 'Acme Property Group' },
                    { k: 'phone', label: 'Phone', type: 'tel', ph: '+1, +44 or +61 …' },
                  ].map((f) => (
                    <div key={f.k}>
                      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">{f.label}</label>
                      <input
                        type={f.type}
                        value={demoForm[f.k]}
                        onChange={(e) => setDemoForm((s) => ({ ...s, [f.k]: e.target.value }))}
                        placeholder={f.ph}
                        data-testid={`astrid-demo-${f.k}`}
                        className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-800 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-white/10 dark:bg-white/5 dark:text-white"
                      />
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400">US, UK or Australian number — include your country code.</p>
                  <button type="submit" disabled={demoLoading} className="mt-1 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-600 disabled:opacity-60" data-testid="astrid-demo-submit">
                    {demoLoading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<>Request my Astrid demo <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                </form>
              </>
            )}
          </div>
        ) : (
        <>
        {/* Conversation */}
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {messages.map((m, i) =>
            m.from === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand px-4 py-2.5 text-sm font-medium text-white">{m.text}</div>
              </div>
            ) : (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <Bot className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 space-y-2.5">
                  <div className="rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
                    {m.text || m.answer}
                  </div>
                  {m.isAnswer && (
                    <>
                      <div className="text-[11px] font-medium text-slate-400">Illustrative data • {m.evidence}</div>
                      <div className="flex flex-wrap gap-2">
                        {m.chips.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => ask(c)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-brand/40 hover:text-brand dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setDemoView(true)}
                        className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-600"
                        data-testid="astrid-drawer-signin"
                      >
                        See Astrid on your data <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  {m.fallback && (
                    <button
                      type="button"
                      onClick={() => setDemoView(true)}
                      data-testid="astrid-drawer-signin-fallback"
                      className="inline-flex w-fit items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-600"
                    >
                      See Astrid on your data <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )
          )}

          {/* Thinking */}
          {thinking && (
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                <Bot className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                {thinking.tools.slice(0, Math.max(thinking.done, 1)).map((t, i) => {
                  const done = i < thinking.done - 1 || thinking.done === thinking.tools.length;
                  return (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-300">
                      {done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />}
                      <span>{t}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Example question groups */}
          {messages.length <= 1 && !thinking && (
            <div className="space-y-4 pt-1">
              {GROUPS.map((g) => (
                <div key={g.title}>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{g.title}</div>
                  <div className="flex flex-col gap-2">
                    {g.questions.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => ask(q)}
                        data-testid="astrid-example-question"
                        className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:text-brand dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200"
                      >
                        {q}
                        <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-brand" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <form
          className="flex items-center gap-2 border-t border-slate-200 px-4 py-3 dark:border-white/10"
          onSubmit={(e) => { e.preventDefault(); ask(input); setInput(''); }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Try an example question…"
            className="h-10 flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-white/10 dark:bg-white/5 dark:text-white"
            data-testid="astrid-drawer-input"
          />
          <button
            type="submit"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            aria-label="Send"
            data-testid="astrid-drawer-send"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
        </>
        )}
      </div>
    </div>
  );
}
